import type { ClosingPackageWorkflowState } from '../types';

/** Three lifecycle groups (Screen Spec §5.1 / Visual Spec Variant B). */
export const CLOSING_PACKAGE_PROGRESS_STEPS = [
  { key: 'formation', title: 'Формирование' },
  { key: 'client', title: 'Согласование' },
  { key: 'originals', title: 'Оригиналы' },
] as const;

export type ClosingPackageProgressStepKey = (typeof CLOSING_PACKAGE_PROGRESS_STEPS)[number]['key'];

export type ClosingPackageWorkflowProgress = {
  /** 0..2 — active lifecycle group index */
  currentStepIndex: number;
  /** 0..100 — Progress bar percent */
  percent: number;
  /** Ant Steps status for each group */
  stepStatuses: Array<'wait' | 'process' | 'finish' | 'error'>;
  currentGroupLabel: string;
};

const STATE_PROGRESS: Record<
  ClosingPackageWorkflowState,
  { stepIndex: number; percent: number }
> = {
  NOT_AVAILABLE: { stepIndex: 0, percent: 0 },
  DRAFT: { stepIndex: 0, percent: 15 },
  FORMED: { stepIndex: 0, percent: 30 },
  SCAN_REVIEW_REQUIRED: { stepIndex: 0, percent: 35 },
  REVISION_REQUIRED: { stepIndex: 0, percent: 40 },
  SCANS_SENT_TO_CLIENT: { stepIndex: 1, percent: 50 },
  WAITING_CLIENT_APPROVAL: { stepIndex: 1, percent: 55 },
  CLIENT_APPROVED: { stepIndex: 1, percent: 65 },
  ORIGINALS_PREPARATION: { stepIndex: 2, percent: 75 },
  ORIGINALS_SENT: { stepIndex: 2, percent: 85 },
  CLIENT_RECEIVED: { stepIndex: 2, percent: 95 },
  COMPLETED: { stepIndex: 2, percent: 100 },
  CANCELLED: { stepIndex: 0, percent: 0 },
};

export function resolveClosingPackageWorkflowProgress(
  workflowState: ClosingPackageWorkflowState | null | undefined
): ClosingPackageWorkflowProgress {
  const state = workflowState ?? 'NOT_AVAILABLE';
  const mapped = STATE_PROGRESS[state] ?? STATE_PROGRESS.NOT_AVAILABLE;
  const { stepIndex, percent } = mapped;

  const stepStatuses: ClosingPackageWorkflowProgress['stepStatuses'] = CLOSING_PACKAGE_PROGRESS_STEPS.map(
    (_, index) => {
      if (state === 'CANCELLED') return 'error';
      if (index < stepIndex) return 'finish';
      if (index === stepIndex) {
        if (state === 'COMPLETED' || state === 'CLIENT_RECEIVED') return 'finish';
        return 'process';
      }
      return 'wait';
    }
  );

  return {
    currentStepIndex: stepIndex,
    percent,
    stepStatuses,
    currentGroupLabel: CLOSING_PACKAGE_PROGRESS_STEPS[stepIndex]?.title ?? '',
  };
}
